import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { can } from '../../utils/permissions';
import MonthlyBarChart from '../../components/MonthlyBarChart';

/*
 * AWS costs and usage (/admin/costs-and-usage, 2026-10-18), for collection
 * managers and system admins (costs:read).
 *
 * One snapshot, collected every 8 hours outside the VPC by
 * pitriot-usage-collector and served by GET /admin/costs-and-usage
 * (routes/costs_usage.py). It is ACCOUNT-WIDE: dev and production share one
 * AWS account, so the costs are both environments'; the resource tables say
 * which environment each row is, and the reader's own is marked.
 *
 * Every part may be missing (the collector names what failed), and the page
 * says so per part rather than failing whole.
 */
const LOW_STORAGE_BYTES = 4 * 1024 ** 3;

function money(lang) {
  const format = new Intl.NumberFormat(lang === 'he' ? 'he-IL' : 'en-GB',
    { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
  return (n) => (n == null ? '—' : format.format(n));
}

/** Whole dollars, '$': what fits over a 28 px bar. */
function moneyShort(lang) {
  const format = new Intl.NumberFormat(lang === 'he' ? 'he-IL' : 'en-GB',
    { style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol', maximumFractionDigits: 0 });
  return (n) => (n == null ? '—' : format.format(n));
}

function bytes(n) {
  if (n == null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = n;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit += 1; }
  return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}

const count = (n) => (n == null ? '—' : Number(n).toLocaleString());
const ms = (n) => (n == null ? '—' : `${Math.round(n)} ms`);
const pct = (n) => (n == null ? '—' : `${Math.round(n)}%`);

function ago(seconds, t) {
  if (seconds == null) return '';
  if (seconds < 3600) return t('agoMinutes', { count: Math.max(1, Math.round(seconds / 60)) });
  return t('agoHours', { count: Math.round(seconds / 3600) });
}

export default function CostsUsage() {
  const { t, i18n } = useTranslation('costs');
  const { user } = useAuth();
  const allowed = can(user, 'costs:read');
  const [state, setState] = useState({ phase: 'loading' });
  const lang = i18n?.language;

  useEffect(() => {
    if (!allowed) return;
    api.get('/admin/costs-and-usage')
      .then(({ data }) => setState({ phase: 'ready', data }))
      .catch((e) => setState({ phase: 'failed', status: e?.response?.status, data: e?.response?.data || {},
        message: e?.message }));
  }, [allowed]);

  if (!allowed) {
    return <p style={{ color: 'var(--c-text-muted-fg)' }} data-testid="costs-restricted">{t('restricted')}</p>;
  }

  return (
    <div>
      <h2>{t('title')}</h2>
      <p style={{ color: 'var(--c-text-muted-fg)', marginTop: 0, maxWidth: 760 }}>{t('intro')}</p>
      {state.phase === 'loading' && <p role="status">{t('loading')}</p>}
      {state.phase === 'failed' && <Failure state={state} t={t} />}
      {state.phase === 'ready' && <Report body={state.data} t={t} lang={lang} />}
    </div>
  );
}

function Failure({ state, t }) {
  const { code, error } = state.data;
  const text = code === 'cloud_only' ? t('cloudOnly')
    : code === 'usage_not_collected' ? t('notCollected')
      : t('unreadable', { error: error || state.message || '' });
  return (
    <p role={code === 'cloud_only' ? 'note' : 'alert'} data-testid="costs-failure" data-code={code || ''}
      style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--c-bg-muted)', maxWidth: 760 }}>
      {text}
    </p>
  );
}

function Report({ body, t, lang }) {
  const snap = body.snapshot || {};
  const fmt = money(lang);
  const when = body.as_of ? new Date(body.as_of).toLocaleString(lang === 'he' ? 'he-IL' : 'en-GB') : '—';
  const mine = body.viewer_environment;
  return (
    <>
      <p style={{ fontSize: 13 }} data-testid="costs-as-of">{t('asOf', { when, ago: ago(body.age_seconds, t) })}</p>
      {body.stale && (
        <p role="alert" data-testid="costs-stale"
          style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--c-warning-soft, var(--c-bg-muted))' }}>
          {t('stale')}
        </p>
      )}
      <p role="note" style={{ fontSize: 13, color: 'var(--c-text-muted-fg)' }} data-testid="costs-account-wide">
        {t('accountWide')}
      </p>

      <Spend costs={snap.costs} t={t} fmt={fmt} short={moneyShort(lang)} />
      <Lambda block={snap.lambda} t={t} mine={mine} />
      <Api rows={snap.api} t={t} mine={mine} />
      <Rds rows={snap.rds} t={t} mine={mine} />
      <Buckets rows={snap.s3} t={t} mine={mine} />

      {snap.errors?.length > 0 && (
        <section data-testid="costs-errors" style={sectionStyle}>
          <h3 style={h3Style}>{t('errors.title')}</h3>
          <ul style={{ margin: 0 }}>
            {snap.errors.map((e) => <li key={`${e.part}-${e.code}`}>{t('errors.item', { part: e.part, code: e.code })}</li>)}
          </ul>
        </section>
      )}
    </>
  );
}

function Spend({ costs, t, fmt, short }) {
  if (!costs) {
    return <section style={sectionStyle}><h3 style={h3Style}>{t('spend.title')}</h3><p>{t('spend.unavailable')}</p></section>;
  }
  const budget = costs.budget?.limit;
  const used = budget ? Math.min(100, (costs.month_to_date / budget) * 100) : null;
  const forecastAt = budget && costs.forecast ? Math.min(100, (costs.forecast.month_total / budget) * 100) : null;
  const over = costs.forecast && budget && costs.forecast.month_total > budget;
  return (
    <section style={sectionStyle} data-testid="costs-spend">
      <h3 style={h3Style}>{t('spend.title')}</h3>
      <p style={{ fontSize: 20, fontWeight: 700, margin: '0 0 4px' }} data-testid="costs-to-date">
        {t('spend.toDate', { amount: fmt(costs.month_to_date) })}
      </p>
      {budget && (
        <>
          <div role="meter" aria-valuemin={0} aria-valuemax={budget} aria-valuenow={costs.month_to_date}
            aria-label={t('spend.ofBudget', { percent: Math.round(used), budget: fmt(budget) })}
            data-testid="costs-meter"
            style={{ position: 'relative', height: 12, borderRadius: 6, background: 'var(--c-bg-muted)', maxWidth: 520 }}>
            <div style={{ width: `${used}%`, height: '100%', borderRadius: 6,
              background: over ? 'var(--c-danger)' : 'var(--c-primary)' }} />
            {forecastAt != null && (
              <div title={t('spend.forecastMark')} data-testid="costs-forecast-mark"
                style={{ position: 'absolute', insetBlock: -3, insetInlineStart: `${forecastAt}%`, width: 2,
                  background: 'var(--c-text-body-fg)' }} />
            )}
          </div>
          <p style={{ fontSize: 13, margin: '4px 0' }}>
            {t('spend.ofBudget', { percent: Math.round(used), budget: fmt(budget) })}
            {costs.budget.source === 'configuration' && <> {t('spend.budgetFromConfig')}</>}
          </p>
        </>
      )}
      <p style={{ fontSize: 13, margin: '4px 0' }} data-testid="costs-forecast">
        {costs.forecast
          ? t('spend.forecast', { total: fmt(costs.forecast.month_total), low: fmt(costs.forecast.low), high: fmt(costs.forecast.high) })
          : t('spend.noForecast')}
      </p>
      <p style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', margin: '4px 0 12px' }}>
        {t('spend.dataThrough', { date: costs.data_through })}
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
        <div style={{ flex: '1 1 300px', minWidth: 0 }}>
          <h4 style={h4Style}>{t('byService.title')}</h4>
          <table style={tableStyle} data-testid="costs-by-service">
            <thead><tr><th style={thStyle}>{t('byService.service')}</th><th style={thStyle}>{t('byService.amount')}</th>
              <th style={thStyle}>{t('byService.share')}</th></tr></thead>
            <tbody>
              {costs.by_service.map((row) => (
                <tr key={row.service}>
                  <td style={tdStyle}>{row.service === 'Other' ? t('byService.other') : row.service}</td>
                  <td style={tdStyle}>{fmt(row.amount)}</td>
                  <td style={tdStyle}>{costs.month_to_date ? pct((row.amount / costs.month_to_date) * 100) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ flex: '1 1 260px', minWidth: 0 }} data-testid="costs-months">
          <h4 style={h4Style}>{t('months.title')}</h4>
          <MonthlyBarChart data={(costs.months || []).map((m) => ({ month: m.month, count: m.total }))}
            emptyMessage={t('months.empty')} formatValue={short}
            reference={budget ? { value: budget, label: t('months.budget', { amount: short(budget) }) } : null} />
        </div>
      </div>
    </section>
  );
}

function Env({ env, mine, t }) {
  return (
    <>
      {t(`env.${env}`, { defaultValue: env })}
      {env === mine && <span style={{ fontSize: 11, color: 'var(--c-text-muted-fg)' }}> {t('yours')}</span>}
    </>
  );
}

const rowStyle = (env, mine) => (env === mine ? { background: 'var(--c-primary-soft-3)' } : undefined);

function Lambda({ block, t, mine }) {
  return (
    <section style={sectionStyle} data-testid="costs-lambda">
      <h3 style={h3Style}>{t('lambda.title')}</h3>
      {!block ? <p>{t('partUnavailable')}</p> : (
        <>
          <p style={{ fontSize: 13 }} data-testid="costs-concurrency">
            {t('lambda.account', { peak: count(block.account?.peak_concurrency_7d),
              limit: count(block.account?.concurrency_limit) })}
          </p>
          <div style={scrollStyle}>
            <table style={tableStyle}>
              <thead><tr>
                <th style={thStyle}>{t('lambda.name')}</th><th style={thStyle}>{t('env.title')}</th>
                <th style={thStyle}>{t('lambda.invocations')} · {t('today')}</th>
                <th style={thStyle}>{t('lambda.invocations')} · {t('week')}</th>
                <th style={thStyle}>{t('lambda.errors')} · {t('week')}</th>
                <th style={thStyle}>{t('lambda.throttles')} · {t('week')}</th>
                <th style={thStyle}>{t('lambda.duration')} · {t('week')}</th>
                <th style={thStyle}>{t('lambda.peak')} · {t('week')}</th>
              </tr></thead>
              <tbody>
                {block.functions.map((fn) => (
                  <tr key={fn.name} style={rowStyle(fn.environment, mine)} data-testid="costs-function">
                    <td style={tdStyle}><code>{fn.name}</code></td>
                    <td style={tdStyle}><Env env={fn.environment} mine={mine} t={t} /></td>
                    <td style={tdStyle}>{count(fn.today.invocations)}</td>
                    <td style={tdStyle}>{count(fn.last_7d.invocations)}</td>
                    <td style={tdStyle}>{count(fn.last_7d.errors)}</td>
                    <td style={{ ...tdStyle, color: fn.last_7d.throttles ? 'var(--c-danger-fg)' : undefined,
                      fontWeight: fn.last_7d.throttles ? 700 : undefined }}>{count(fn.last_7d.throttles)}</td>
                    <td style={tdStyle}>{ms(fn.last_7d.duration_ms.avg)} / {ms(fn.last_7d.duration_ms.p99)}</td>
                    <td style={tdStyle}>{count(fn.last_7d.peak_concurrency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function Api({ rows, t, mine }) {
  return (
    <section style={sectionStyle} data-testid="costs-api">
      <h3 style={h3Style}>{t('api.title')}</h3>
      {!rows ? <p>{t('partUnavailable')}</p> : (
        <div style={scrollStyle}>
          <table style={tableStyle}>
            <thead><tr>
              <th style={thStyle}>API</th><th style={thStyle}>{t('env.title')}</th>
              <th style={thStyle}>{t('api.requests')} · {t('today')}</th>
              <th style={thStyle}>{t('api.requests')} · {t('week')}</th>
              <th style={thStyle}>4xx · {t('week')}</th><th style={thStyle}>5xx · {t('week')}</th>
              <th style={thStyle}>{t('api.latency')} · {t('week')}</th>
            </tr></thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.api_id} style={rowStyle(a.environment, mine)}>
                  <td style={tdStyle}><code>{a.api_id}</code></td>
                  <td style={tdStyle}><Env env={a.environment} mine={mine} t={t} /></td>
                  <td style={tdStyle}>{count(a.today.requests)}</td>
                  <td style={tdStyle}>{count(a.last_7d.requests)}</td>
                  <td style={tdStyle}>{count(a.last_7d.errors_4xx)}</td>
                  <td style={{ ...tdStyle, color: a.last_7d.errors_5xx ? 'var(--c-danger-fg)' : undefined }}>{count(a.last_7d.errors_5xx)}</td>
                  <td style={tdStyle}>{ms(a.last_7d.latency_ms.avg)} / {ms(a.last_7d.latency_ms.p99)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Rds({ rows, t, mine }) {
  return (
    <section style={sectionStyle} data-testid="costs-rds">
      <h3 style={h3Style}>{t('rds.title')}</h3>
      {!rows ? <p>{t('partUnavailable')}</p> : (
        <div style={scrollStyle}>
          <table style={tableStyle}>
            <thead><tr>
              <th style={thStyle}>{t('rds.instance')}</th><th style={thStyle}>{t('env.title')}</th>
              <th style={thStyle}>{t('rds.cpu')} · {t('week')}</th><th style={thStyle}>{t('rds.connections')} · {t('week')}</th>
              <th style={thStyle}>{t('rds.storage')}</th><th style={thStyle}>{t('rds.memory')}</th>
              <th style={thStyle}>{t('rds.credits')}</th>
            </tr></thead>
            <tbody>
              {rows.map((db) => {
                const free = db.last_7d.free_storage_bytes_min;
                const low = free != null && free < LOW_STORAGE_BYTES;
                return (
                  <tr key={db.instance} style={rowStyle(db.environment, mine)}>
                    <td style={tdStyle}><code>{db.instance}</code></td>
                    <td style={tdStyle}><Env env={db.environment} mine={mine} t={t} /></td>
                    <td style={tdStyle}>{pct(db.last_7d.cpu_pct.avg)} / {pct(db.last_7d.cpu_pct.max)}</td>
                    <td style={tdStyle}>{count(db.last_7d.connections_max)}</td>
                    <td style={{ ...tdStyle, color: low ? 'var(--c-danger-fg)' : undefined }} data-testid="costs-free-storage">
                      {bytes(free)}{db.allocated_gb ? ` / ${db.allocated_gb} GB` : ''}{low && <> · {t('rds.lowStorage')}</>}
                    </td>
                    <td style={tdStyle}>{bytes(db.last_7d.freeable_memory_bytes_min)}</td>
                    <td style={tdStyle}>{count(db.last_7d.cpu_credit_balance_min)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Buckets({ rows, t, mine }) {
  return (
    <section style={sectionStyle} data-testid="costs-s3">
      <h3 style={h3Style}>{t('s3.title')}</h3>
      {!rows ? <p>{t('partUnavailable')}</p> : (
        <div style={scrollStyle}>
          <table style={tableStyle}>
            <thead><tr>
              <th style={thStyle}>{t('s3.bucket')}</th><th style={thStyle}>{t('env.title')}</th>
              <th style={thStyle}>{t('s3.size')}</th><th style={thStyle}>{t('s3.objects')}</th><th style={thStyle}>{t('s3.asOf')}</th>
            </tr></thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.bucket} style={rowStyle(b.environment, mine)}>
                  <td style={tdStyle}><code style={{ overflowWrap: 'anywhere' }}>{b.bucket}</code></td>
                  <td style={tdStyle}><Env env={b.environment} mine={mine} t={t} /></td>
                  <td style={tdStyle}>{bytes(b.size_bytes)}</td>
                  <td style={tdStyle}>{count(b.objects)}</td>
                  <td style={tdStyle}>{b.as_of || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const sectionStyle = {
  border: '1px solid var(--c-border)', borderRadius: 10, padding: '12px 14px', marginBlockEnd: 14,
  background: 'var(--c-surface)',
};
const h3Style = { margin: '0 0 8px', fontSize: 16 };
const h4Style = { margin: '0 0 6px', fontSize: 13 };
const scrollStyle = { overflowX: 'auto' };
const tableStyle = { borderCollapse: 'collapse', width: '100%', fontSize: 13 };
const thStyle = { textAlign: 'start', padding: '4px 8px', borderBlockEnd: '1px solid var(--c-border)', whiteSpace: 'nowrap' };
const tdStyle = { padding: '4px 8px', borderBlockEnd: '1px solid var(--c-bg-subtle)', whiteSpace: 'nowrap' };
